/**
 * Acqs property image uploader — drag-and-drop multi-file queue, two-phase
 * upload via TmpFile.
 *
 * Mounts on `[data-image-uploader]` cards (rendered by
 * `acqs/templates/acqs/_property_images_card.html`).  Each dropped file
 * uploads immediately to `[data-queue-add-url]`, which stores the bytes as a
 * `TmpFile` and returns the rendered queue row partial
 * (`acqs/_image_queue_row.html`).  A skeleton placeholder is shown while the
 * upload is in flight.  Save POSTs JSON `{items: [{tmp_file_id, room,
 * caption}, ...]}` to `[data-upload-url]`, which promotes each TmpFile to an
 * AcqImage with metadata and returns the refreshed images card.
 *
 * Row markup (server-rendered) and room choices live in Django templates and
 * model constants — no HTML strings or duplicated room lists in JS.
 */

(function () {

    var AUTO_LABEL_KEY = 'acqs.autoLabelRooms';

    function autoLabelEnabled() {
        try { return localStorage.getItem(AUTO_LABEL_KEY) !== '0'; }
        catch (e) { return true; }
    }

    function getCsrfToken() {
        return window._cc && window._cc.getCsrfToken ? window._cc.getCsrfToken() : '';
    }

    function buildPlaceholder(filename) {
        // Skeleton row shown while the upload is in flight. Mirrors the layout
        // of `_image_queue_row.html`: square thumb on the left, two stacked
        // rows on the right.  Replaced by the server-rendered partial on
        // success, or marked failed on error.
        var row = document.createElement('div');
        row.className = 'list-group-item';
        row.setAttribute('data-image-row', '');
        row.setAttribute('data-image-pending', '');
        row.title = filename;
        row.innerHTML = (
            '<div class="file-uploader-thumb skeleton" data-image-thumb-skeleton></div>' +
            '<div class="list-item-content d-flex flex-column gap-2">' +
                '<div class="text-muted fs-xs">' +
                    '<i class="fa-solid fa-spinner fa-spin"></i> Uploading…' +
                '</div>' +
                '<div class="text-truncate fs-xs text-faint" data-image-pending-name></div>' +
            '</div>'
        );
        row.querySelector('[data-image-pending-name]').textContent = filename;
        return row;
    }

    function markPlaceholderFailed(row, message) {
        row.removeAttribute('data-image-pending');
        row.setAttribute('data-image-failed', '');
        var status = row.querySelector('.text-muted');
        if (status) {
            status.classList.remove('text-muted');
            status.classList.add('text-error');
            // `message` can now come from the server (the queue-add rejection
            // text), so it goes in as text, never markup.
            status.innerHTML = '<i class="fa-solid fa-circle-exclamation"></i> ';
            status.appendChild(document.createTextNode(message));
        }
    }

    // The queue-add view answers a rejected image with a SHORT plain-text 400.
    // Anything else — a 403/500 HTML error page, a proxy's error body — is not
    // a message for the rep, so fall back to the generic wording rather than
    // painting a whole error document into the queue row and the toast.
    function serverRejectionMessage(xhr) {
        if (xhr.status !== 400) return '';
        var text = (xhr.responseText || '').trim();
        if (!text || text.length > 200 || text.charAt(0) === '<') return '';
        return text;
    }

    function urlWithLiveRentalGalleryQuery(card, baseUrl) {
        var gallery = card.querySelector('[data-rental-photo-query]');
        if (!gallery) return baseUrl;
        var query = gallery.getAttribute('data-rental-photo-query');
        return baseUrl.split('?')[0] + (query ? '?' + query : '');
    }

    function init(card) {
        if (!card || card.hasAttribute('data-image-init')) return;
        card.setAttribute('data-image-init', '');

        var queueAddUrl = card.getAttribute('data-queue-add-url');
        var uploadUrl = card.getAttribute('data-upload-url');
        var acqLeadId = card.getAttribute('data-acq-lead-id');
        var dealId = card.getAttribute('data-deal-id');
        var dropzone = card.querySelector('[data-image-dropzone]');
        var fileInput = card.querySelector('[data-image-input]');
        var queueEl = card.querySelector('[data-image-queue]');
        var saveBtn = card.querySelector('[data-image-save]');
        var cancelBtn = card.querySelector('[data-image-cancel]');
        var countEl = card.querySelector('[data-image-count]');
        var autoToggle = card.querySelector('[data-auto-label-toggle]');
        if (autoToggle) {
            autoToggle.checked = autoLabelEnabled();
            autoToggle.addEventListener('change', function () {
                try { localStorage.setItem(AUTO_LABEL_KEY, autoToggle.checked ? '1' : '0'); }
                catch (e) { /* ignore */ }
                queueEl.querySelectorAll('[data-image-room]').forEach(function (roomSel) {
                    if (!autoToggle.checked && roomSel.value === 'auto') {
                        roomSel.value = 'exterior';
                    } else if (autoToggle.checked && roomSel.dataset.autoUntouched === '1' && roomSel.value === 'exterior') {
                        roomSel.value = 'auto';
                    }
                });
            });
        }

        function isAutoOn() { return autoToggle ? autoToggle.checked : autoLabelEnabled(); }

        if (!dropzone || !fileInput || !queueEl || !saveBtn) return;

        function readyRows() {
            return queueEl.querySelectorAll('[data-tmp-file-id]');
        }

        function pendingRows() {
            return queueEl.querySelectorAll('[data-image-pending]');
        }

        function refreshState() {
            var ready = readyRows().length;
            var pending = pendingRows().length;
            if (countEl) countEl.textContent = ready;
            saveBtn.disabled = ready === 0 || pending > 0;
        }

        function uploadFile(file) {
            var placeholder = buildPlaceholder(file.name);
            queueEl.appendChild(placeholder);
            refreshState();

            var formData = new FormData();
            formData.append('file', file);

            var xhr = new XMLHttpRequest();
            xhr.open('POST', urlWithLiveRentalGalleryQuery(card, queueAddUrl));
            xhr.setRequestHeader('X-CSRFToken', getCsrfToken());
            xhr.onload = function () {
                if (xhr.status >= 200 && xhr.status < 300 && xhr.responseText.trim()) {
                    var tmp = document.createElement('div');
                    tmp.innerHTML = xhr.responseText.trim();
                    var newRow = tmp.firstElementChild;
                    if (newRow) {
                        placeholder.replaceWith(newRow);
                        var roomSel = newRow.querySelector('[data-image-room]');
                        if (roomSel) roomSel.dataset.autoUntouched = '1';
                        if (isAutoOn()) {
                            if (roomSel) roomSel.value = 'auto';
                        }
                    } else {
                        markPlaceholderFailed(placeholder, 'Upload failed');
                    }
                } else {
                    var errMessage = serverRejectionMessage(xhr);
                    markPlaceholderFailed(placeholder, errMessage || 'Upload failed');
                    if (window.SeeMe && SeeMe.toast) {
                        SeeMe.toast.error(
                            'Upload failed: ' + file.name + (errMessage ? ' — ' + errMessage : '')
                        );
                    }
                }
                refreshState();
            };
            xhr.onerror = function () {
                markPlaceholderFailed(placeholder, 'Upload failed');
                if (window.SeeMe && SeeMe.toast) {
                    SeeMe.toast.error('Upload failed: ' + file.name);
                }
                refreshState();
            };
            xhr.send(formData);
        }

        function addFiles(fileList) {
            for (var i = 0; i < fileList.length; i++) {
                var f = fileList[i];
                if (!f.type || f.type.indexOf('image/') !== 0) {
                    if (window.SeeMe && SeeMe.toast) {
                        SeeMe.toast.error('Skipped non-image file: ' + f.name);
                    }
                    continue;
                }
                uploadFile(f);
            }
        }

        function removeRow(row) {
            var deleteUrl = row.getAttribute('data-tmp-file-delete-url');
            row.remove();
            refreshState();
            if (!deleteUrl) return;
            // Best-effort cleanup; the daily TmpFile sweeper would catch it anyway.
            var xhr = new XMLHttpRequest();
            xhr.open('DELETE', deleteUrl);
            xhr.setRequestHeader('X-CSRFToken', getCsrfToken());
            xhr.send();
        }

        function clearQueue() {
            // Remove each row individually so TmpFiles get DELETEd.
            var rows = queueEl.querySelectorAll('[data-image-row]');
            for (var i = 0; i < rows.length; i++) removeRow(rows[i]);
        }

        // Browse link + dropzone click → open file picker
        dropzone.addEventListener('click', function (e) {
            if (e.target === fileInput) return;
            e.preventDefault();
            fileInput.click();
        });

        fileInput.addEventListener('change', function () {
            addFiles(fileInput.files);
            fileInput.value = '';
        });

        // Drag-and-drop on dropzone
        var dragCounter = 0;
        dropzone.addEventListener('dragenter', function (e) {
            e.preventDefault();
            dragCounter++;
            dropzone.classList.add('file-uploader-dropzone-active');
        });
        dropzone.addEventListener('dragover', function (e) { e.preventDefault(); });
        dropzone.addEventListener('dragleave', function () {
            dragCounter--;
            if (dragCounter === 0) dropzone.classList.remove('file-uploader-dropzone-active');
        });
        dropzone.addEventListener('drop', function (e) {
            e.preventDefault();
            dragCounter = 0;
            dropzone.classList.remove('file-uploader-dropzone-active');
            addFiles(e.dataTransfer.files);
        });

        // Queue row remove
        queueEl.addEventListener('click', function (e) {
            var btn = e.target.closest('[data-image-remove]');
            if (!btn) return;
            var row = btn.closest('[data-image-row]');
            if (row) removeRow(row);
        });

        queueEl.addEventListener('change', function (e) {
            if (e.target.matches('[data-image-room]')) {
                delete e.target.dataset.autoUntouched;
            }
        });

        // Cancel — discard queue (the data-collapse-target attribute on the
        // button toggles the trigger/form visibility via SeeMe's global handler).
        if (cancelBtn) {
            cancelBtn.addEventListener('click', function () {
                clearQueue();
            });
        }

        // Save — POST JSON of staged TmpFiles + per-row metadata
        saveBtn.addEventListener('click', function () {
            var rows = readyRows();
            if (rows.length === 0) return;

            saveBtn.disabled = true;

            var items = [];
            rows.forEach(function (row) {
                var roomEl = row.querySelector('[data-image-room]');
                var capEl = row.querySelector('[data-image-caption]');
                items.push({
                    tmp_file_id: parseInt(row.getAttribute('data-tmp-file-id'), 10),
                    room: roomEl ? roomEl.value : 'other',
                    caption: capEl ? capEl.value : '',
                });
            });

            var payload = {items: items, auto_label: isAutoOn()};
            if (acqLeadId) payload.acq_lead = parseInt(acqLeadId, 10);
            if (dealId) payload.deal = parseInt(dealId, 10);

            fetch(urlWithLiveRentalGalleryQuery(card, uploadUrl), {
                method: 'POST',
                body: JSON.stringify(payload),
                headers: {
                    'X-CSRFToken': getCsrfToken(),
                    'Content-Type': 'application/json',
                },
                credentials: 'same-origin',
            }).then(function (r) {
                if (!r.ok) throw new Error('Upload failed (' + r.status + ')');
                return r.text().then(function (html) {
                    return { html: html, headers: r.headers };
                });
            }).then(function (res) {
                var parent = card.parentNode;
                if (!parent) return;
                var tmp = document.createElement('div');
                tmp.innerHTML = res.html.trim();
                var newCard = tmp.firstElementChild;
                if (!newCard) {
                    saveBtn.disabled = false;
                    return;
                }
                card.replaceWith(newCard);
                if (window.htmx) htmx.process(newCard);
                // htmx.process() fires no lifecycle event, so browser-local
                // datetime rendering must be re-applied by hand on this
                // manual-fetch swap (same as acqs-ws.js).
                if (window._cc && window._cc.applyBrowserLocalDatetime) {
                    window._cc.applyBrowserLocalDatetime(newCard);
                }
                if (window._cc && window._cc.initRentalPhotoSorting) {
                    window._cc.initRentalPhotoSorting(newCard);
                }

                // Swap any OOB fragments appended after the main card (e.g. count badges
                // in the outer tabbed wrapper that are outside the uploader's scope).
                var oobEls = tmp.querySelectorAll('[hx-swap-oob],[data-hx-swap-oob]');
                var swappedTabsStrip = false;
                oobEls.forEach(function (el) {
                    var targetId = (el.getAttribute('hx-swap-oob') || el.getAttribute('data-hx-swap-oob') || '').replace(/^outerHTML:/, '');
                    var target = targetId ? document.querySelector(targetId) : null;
                    if (target) {
                        target.replaceWith(el);
                        if (el.matches('[data-tabs-collapse]') || el.querySelector('[data-tabs-collapse]')) {
                            swappedTabsStrip = true;
                        }
                    }
                });
                // This manual OOB swap bypasses htmx, so htmx:afterSettle never fires —
                // tabs-collapse.js only re-measures on load/resize/that event, so a
                // swapped-in tabs strip (e.g. the rental photo category tabs) would show
                // fully expanded on a narrow screen. A synthetic afterSettle would reach
                // listeners that read e.detail, so trigger its resize path instead.
                if (swappedTabsStrip) {
                    window.dispatchEvent(new Event('resize'));
                }

                // Bridge HX-Trigger toast (manual fetch — HTMX doesn't see this response)
                var trig = res.headers.get('HX-Trigger');
                if (trig && window.SeeMe && SeeMe.toast) {
                    try {
                        var data = JSON.parse(trig);
                        if (data.showToast && data.showToast.message) {
                            var fn = data.showToast.type === 'error' ? 'error' : 'success';
                            SeeMe.toast[fn](data.showToast.message);
                        }
                    } catch (e) { /* ignore malformed */ }
                }
            }).catch(function (err) {
                saveBtn.disabled = false;
                if (window.SeeMe && SeeMe.toast) SeeMe.toast.error(err.message || 'Upload failed');
            });
        });

        refreshState();
    }

    function scan(root) {
        var scope = root || document;
        if (scope.querySelectorAll) {
            scope.querySelectorAll('[data-image-uploader]').forEach(init);
        }
        if (scope.matches && scope.matches('[data-image-uploader]')) init(scope);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () { scan(); });
    } else {
        scan();
    }
    document.body.addEventListener('htmx:afterSwap', function (e) {
        scan(e.detail.elt || document);
    });
    document.body.addEventListener('change', function (e) {
        if (!e.target.matches('[data-image-layout-toggle] input[type="radio"]')) return;
        // Pair toggle -> grid by containment so every surface that renders
        // `_property_images_grid.html` (editing card, read-only SOW modal)
        // flips only its OWN grid.
        var layoutScope = e.target.closest('[data-image-layout-scope]');
        var gallery = layoutScope && layoutScope.querySelector('[data-image-gallery]');
        if (gallery) {
            gallery.setAttribute('data-image-layout', e.target.value);
            gallery.classList.toggle('photo-grid-sections--flat', e.target.value === 'flat');
        }
    });

    // The read-only photo viewer is fetched on open (`data-overlay-url` →
    // `acqs:property-images-modal`) and lands in <body>, so there is no longer
    // a modal nested in the details card to re-parent, open by hand, or tear
    // down when that card is swapped.

    var observer = new MutationObserver(function () {
        scan();
    });
    observer.observe(document.body, { childList: true, subtree: true });

})();
